import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaLock, 
  FaCheckCircle, 
  FaChevronLeft, 
  FaChevronRight, 
  FaPlay, 
  FaBookOpen,
  FaCloud,
  FaServer,
  FaDatabase,
  FaShieldAlt,
  FaNetworkWired,
  FaCogs,
  FaTerminal
} from 'react-icons/fa';
import apiClient from './utils/apiClient';
import Navbar from './NavbarComponent';
import './CourseContent.css';

const defaultAwsCurriculum = [
  {
    id: 1,
    title: '1. Cloud Computing Foundations & AWS Global Infrastructure',
    description: 'Understand cloud models (IaaS, PaaS, SaaS) and AWS Regions, Availability Zones, and Edge Locations.',
    content: (
      <div>
        <p>
          Cloud computing is the on-demand delivery of IT resources over the internet with pay-as-you-go pricing. 
          Instead of buying and maintaining physical data centers, you access compute power, storage, and databases from Amazon Web Services (AWS).
        </p>
        <h4>Core Infrastructure Concepts:</h4>
        <ul>
          <li><strong>AWS Regions:</strong> Physical geographical locations around the world containing multiple isolated Availability Zones (e.g., <code>ap-south-1</code> in Mumbai).</li>
          <li><strong>Availability Zones (AZs):</strong> One or more discrete data centers with redundant power, networking, and connectivity. Designed for fault tolerance and high availability.</li>
          <li><strong>Edge Locations & CloudFront:</strong> Over 400+ points of presence used by AWS CloudFront to cache content closer to end users for ultra-low latency.</li>
          <li><strong>Shared Responsibility Model:</strong> AWS manages security <em>of</em> the cloud (hardware, facilities, global networks), while you manage security <em>in</em> the cloud (data, OS, firewall, IAM).</li>
        </ul>
      </div>
    ),
    question: 'Describe how designing an architecture across multiple Availability Zones protects against single-point-of-failure in AWS.'
  },
  {
    id: 2,
    title: '2. Compute: Amazon EC2, AMI & Auto Scaling',
    description: 'Deploy virtual servers, configure Elastic Compute Cloud (EC2), security groups, and key pairs.',
    content: (
      <div>
        <p>
          Amazon Elastic Compute Cloud (Amazon EC2) provides scalable virtual computing capacity in the AWS Cloud.
        </p>
        <h4>Key EC2 Components:</h4>
        <ul>
          <li><strong>Amazon Machine Image (AMI):</strong> Pre-configured template containing the OS, software, and application servers required to launch an instance (e.g., Amazon Linux 2023, Ubuntu 22.04).</li>
          <li><strong>Instance Types:</strong> General Purpose (t3/t4g), Compute Optimized (c7g), Memory Optimized (r7g), and Storage Optimized.</li>
          <li><strong>Security Groups:</strong> Virtual firewalls controlling inbound and outbound traffic at the instance level (Stateful rules).</li>
          <li><strong>Auto Scaling Groups (ASG):</strong> Automatically adjusts compute capacity up or down according to traffic demand, ensuring cost efficiency and high availability.</li>
        </ul>
      </div>
    ),
    question: 'Configure an EC2 security group that permits incoming HTTPS traffic on port 443 and SSH access on port 22 only from your specific IP.'
  },
  {
    id: 3,
    title: '3. Serverless Compute: AWS Lambda & API Gateway',
    description: 'Execute event-driven code without provisioning servers using AWS Lambda and Amazon API Gateway.',
    content: (
      <div>
        <p>
          Serverless allows you to build and run applications and services without thinking about servers. AWS handles server management, capacity provisioning, and patching automatically.
        </p>
        <h4>Key Concepts:</h4>
        <ul>
          <li><strong>AWS Lambda:</strong> Run code in response to events (HTTP requests, S3 file uploads, DynamoDB streams) with sub-second billing.</li>
          <li><strong>Amazon API Gateway:</strong> A fully managed service that makes it easy for developers to create, publish, maintain, monitor, and secure REST and WebSocket APIs at scale.</li>
          <li><strong>Cold Starts:</strong> The brief initialization latency when a serverless function is invoked for the first time or after a period of inactivity.</li>
        </ul>
      </div>
    ),
    question: 'Write an AWS Lambda handler function in Node.js or Python that accepts an incoming JSON payload and stores an item in a DynamoDB table.'
  },
  {
    id: 4,
    title: '4. Storage Solutions: Amazon S3 & Elastic Block Store (EBS)',
    description: 'Master object storage with Amazon S3 and block-level storage with Amazon EBS volumes.',
    content: (
      <div>
        <p>
          AWS offers reliable, scalable, and secure cloud storage options designed for high durability and performance.
        </p>
        <h4>Comparison of Core Storage Types:</h4>
        <ul>
          <li><strong>Amazon S3 (Simple Storage Service):</strong> Highly durable (99.999999999% - 11 9s) object storage for files, static websites, backups, and data lakes. Uses Buckets and Objects.</li>
          <li><strong>S3 Storage Classes:</strong> S3 Standard, S3 Intelligent-Tiering, S3 Glacier, and Deep Archive for cost-effective long-term data retention.</li>
          <li><strong>Amazon EBS (Elastic Block Store):</strong> Persistent block-level storage volumes attached to EC2 instances (SSD gp3/io2, HDD st1).</li>
          <li><strong>Amazon EFS (Elastic File System):</strong> Managed serverless network file system (NFS) that can be mounted across hundreds of EC2 instances simultaneously.</li>
        </ul>
      </div>
    ),
    question: 'How do you host a production React application statically on Amazon S3 with an SSL certificate provided by AWS Certificate Manager and CloudFront?'
  },
  {
    id: 5,
    title: '5. Networking: Amazon VPC, Subnets & Gateways',
    description: 'Build secure, isolated virtual networks in the cloud with Virtual Private Cloud (VPC).',
    content: (
      <div>
        <p>
          Amazon VPC lets you provision a logically isolated section of the AWS Cloud where you can launch AWS resources in a virtual network you define.
        </p>
        <h4>VPC Architecture Checklist:</h4>
        <ul>
          <li><strong>CIDR Blocks:</strong> Define private IP address ranges (e.g., <code>10.0.0.0/16</code>).</li>
          <li><strong>Public vs Private Subnets:</strong> Public subnets route traffic to the Internet Gateway (IGW). Private subnets route outbound traffic via a NAT Gateway.</li>
          <li><strong>Route Tables:</strong> A set of rules (routes) used to determine where network traffic is directed.</li>
          <li><strong>Network Access Control Lists (NACLs):</strong> Stateless subnet-level boundary firewalls.</li>
        </ul>
      </div>
    ),
    question: 'Design a two-tier VPC architecture with public subnets for web load balancers and private subnets for database clusters.'
  },
  {
    id: 6,
    title: '6. Databases: Amazon RDS, DynamoDB & ElastiCache',
    description: 'Deploy relational databases (RDS/Aurora) and high-speed NoSQL databases (DynamoDB).',
    content: (
      <div>
        <p>
          AWS provides purpose-built database engines tailored to specific application data access patterns.
        </p>
        <h4>Database Choices:</h4>
        <ul>
          <li><strong>Amazon RDS:</strong> Managed relational databases supporting PostgreSQL, MySQL, MariaDB, Oracle, and SQL Server with automated backups and Multi-AZ replication.</li>
          <li><strong>Amazon Aurora:</strong> High-performance MySQL and PostgreSQL-compatible cloud-native database delivering up to 5x throughput.</li>
          <li><strong>Amazon DynamoDB:</strong> Fully managed, serverless key-value and document NoSQL database with single-digit millisecond latency at any scale.</li>
          <li><strong>Amazon ElastiCache:</strong> In-memory caching service using Redis or Memcached to accelerate database queries.</li>
        </ul>
      </div>
    ),
    question: 'When would you choose Amazon DynamoDB over Amazon RDS for a high-volume IoT telemetry application?'
  },
  {
    id: 7,
    title: '7. Identity & Security: AWS IAM & Secrets Manager',
    description: 'Implement least privilege access, IAM Roles, Policies, Multi-Factor Authentication (MFA), and Secrets.',
    content: (
      <div>
        <p>
          Security is Job Zero at AWS. AWS Identity and Access Management (IAM) enables you to manage access to AWS services and resources securely.
        </p>
        <h4>Core IAM Principles:</h4>
        <ul>
          <li><strong>IAM Users & Groups:</strong> Individual accounts and collections of users sharing common permissions.</li>
          <li><strong>IAM Roles:</strong> Temporary credentials assumed by trusted entities, such as an EC2 instance or Lambda function, eliminating hardcoded keys.</li>
          <li><strong>IAM Policies:</strong> JSON permission documents defining what Actions are Allowed or Denied on specific Resources.</li>
          <li><strong>Principle of Least Privilege:</strong> Granting only the exact permissions needed to perform a task and nothing more.</li>
        </ul>
      </div>
    ),
    question: 'Write a sample IAM JSON policy that allows read-only access (`s3:GetObject` and `s3:ListBucket`) to a specific bucket named `my-company-assets`.'
  },
  {
    id: 8,
    title: '8. DevOps & Containers: Docker, ECS & EKS',
    description: 'Containerized microservices orchestration with Elastic Container Service (ECS) and Kubernetes (EKS).',
    content: (
      <div>
        <p>
          Modern applications are built using containers to ensure portability, rapid deployment, and environment parity.
        </p>
        <h4>Container Toolset on AWS:</h4>
        <ul>
          <li><strong>Amazon ECR (Elastic Container Registry):</strong> Secure, private Docker container image registry.</li>
          <li><strong>Amazon ECS (Elastic Container Service):</strong> Highly scalable, fast container management service with AWS Fargate (serverless compute for containers).</li>
          <li><strong>Amazon EKS (Elastic Kubernetes Service):</strong> Managed Kubernetes service for enterprise containerized workloads.</li>
        </ul>
      </div>
    ),
    question: 'Explain the difference between launching containers on ECS with EC2 launch type versus AWS Fargate serverless launch type.'
  },
  {
    id: 9,
    title: '9. Monitoring & Observability: Amazon CloudWatch & CloudTrail',
    description: 'Monitor application health, set up metric alarms, review system logs, and audit user actions.',
    content: (
      <div>
        <p>
          Continuous observability ensures you maintain high availability, detect performance bottlenecks early, and adhere to compliance standards.
        </p>
        <h4>Monitoring Services:</h4>
        <ul>
          <li><strong>Amazon CloudWatch:</strong> Collects operational data in the form of logs, metrics, and events. Create automated alarms to reboot crashed instances or scale up.</li>
          <li><strong>AWS CloudTrail:</strong> Records account activity, API calls, and console actions for security auditing and compliance tracking.</li>
          <li><strong>AWS X-Ray:</strong> Distributed tracing system for analyzing and debugging production microservices applications.</li>
        </ul>
      </div>
    ),
    question: 'Set up a CloudWatch Alarm that triggers an Amazon SNS notification to your dev team whenever EC2 CPU utilization exceeds 80% for 5 consecutive minutes.'
  },
  {
    id: 10,
    title: '10. CI/CD & Deployment: CodePipeline & Production Architecture',
    description: 'Automate build, test, and deployment workflows and learn the AWS Well-Architected Framework.',
    content: (
      <div>
        <p>
          Continuous Integration and Continuous Delivery (CI/CD) pipelines allow engineering teams to release bug fixes and features rapidly and reliably.
        </p>
        <h4>Deployment Pipeline Tools:</h4>
        <ul>
          <li><strong>AWS CodePipeline:</strong> Fully managed continuous delivery service that models, visualizes, and automates software release phases.</li>
          <li><strong>AWS CodeBuild:</strong> Managed build service that compiles source code, runs automated unit tests, and produces deployable artifacts.</li>
          <li><strong>AWS Elastic Beanstalk:</strong> Platform-as-a-Service (PaaS) to deploy applications written in Java, Python, Node.js, and Docker by simply uploading code.</li>
          <li><strong>AWS Well-Architected Pillars:</strong> Operational Excellence, Security, Reliability, Performance Efficiency, Cost Optimization, and Sustainability.</li>
        </ul>
      </div>
    ),
    question: 'Explain how you would architect a resilient, cost-effective full-stack web application with React frontend, Django/Node backend, and PostgreSQL database on AWS.'
  }
];

const CourseAWS = ({ isLoggedIn, setIsLoggedIn, userRole, handleLogout, username = 'Student' }) => {
  const [topics, setTopics] = useState(defaultAwsCurriculum);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [unlockedIndex, setUnlockedIndex] = useState(() => {
    const saved = localStorage.getItem(`aws_progress_${(username || 'student').toLowerCase()}`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(`aws_progress_${(username || 'student').toLowerCase()}`, unlockedIndex.toString());
  }, [unlockedIndex, username]);

  useEffect(() => {
    if (!isLoggedIn) navigate('/LoginPage');
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await apiClient('compiler/content/', 'GET');
        // If backend has AWS content (language id or category), load it
        if (Array.isArray(data)) {
          const filteredTopics = data.filter((topic) => topic.language === 6 || topic.language_name?.toLowerCase().includes('aws'));
          if (filteredTopics.length > 0) {
            const sortedTopics = filteredTopics.sort((a, b) => a.position - b.position);
            setTopics(sortedTopics);
          }
        }
      } catch (error) {
        console.warn('Using built-in AWS curriculum:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTopics();
  }, []);

  const handleTopicClick = (index) => {
    if (index <= unlockedIndex) setCurrentIndex(index);
  };

  const handleNext = () => {
    if (currentIndex < topics.length - 1 && currentIndex < unlockedIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));

  const handleCompleteChapter = () => {
    if (currentIndex === unlockedIndex && unlockedIndex < topics.length - 1) {
      setUnlockedIndex(unlockedIndex + 1);
    }
    if (currentIndex < topics.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  if (loading) {
    return <div className="text-center py-5">Loading AWS Cloud Curriculum...</div>;
  }

  const currentLesson = topics[currentIndex] || topics[0];

  return (
    <>
      <Navbar
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        username={username}
        userRole={userRole}
        handleLogout={handleLogout}
      />

      <div className="cc-root">
        {/* SIDEBAR */}
        <aside className="cc-sidebar">
          <div className="cc-sidebar-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaCloud style={{ color: '#f59e0b' }} /> AWS Cloud Curriculum
          </div>
          <ul className="cc-topic-list">
            {topics.map((topic, index) => {
              const isLocked = index > unlockedIndex;
              const isCompleted = index < unlockedIndex;
              const isActive = index === currentIndex;

              return (
                <li
                  key={index}
                  className={`cc-topic-item ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                  onClick={() => handleTopicClick(index)}
                >
                  {isLocked ? (
                    <FaLock size={14} />
                  ) : isCompleted ? (
                    <FaCheckCircle color="#10b981" />
                  ) : (
                    <FaBookOpen style={{ color: '#f59e0b' }} />
                  )}
                  <span>{topic.title}</span>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* MAIN CONTENT AREA */}
        <main className="cc-main">
          <div className="cc-content-card">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#fffbeb', border: '1px solid #fde68a', color: '#b45309', padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 700, marginBottom: '16px' }}>
              <FaCloud /> AWS Cloud Practitioner & Solutions Architect
            </div>
            
            <h1>{currentLesson.title}</h1>
            
            <div className="cc-text-content">
              {currentLesson.content || 'Lesson content is loading...'}
            </div>

            {currentLesson.question && (
              <div className="cc-question-box" style={{ background: '#0f172a', color: '#ffffff', borderRadius: '12px', padding: '20px', marginTop: '30px' }}>
                <div className="cc-question-text">
                  <span className="d-block small mb-2" style={{ color: '#f59e0b', fontWeight: 800, letterSpacing: '1px' }}>
                    <FaTerminal style={{ marginRight: '6px' }} /> PRACTICAL LAB CHALLENGE
                  </span>
                  <p style={{ margin: 0, fontSize: '0.95rem', color: '#e2e8f0', lineHeight: '1.6' }}>
                    {currentLesson.question}
                  </p>
                </div>
                <button
                  className="cc-btn-start"
                  style={{ marginTop: '16px', background: '#f59e0b', color: '#0f172a', border: 'none', fontWeight: 700, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  onClick={handleCompleteChapter}
                >
                  <FaCheckCircle size={14} /> Mark Chapter Complete & Unlock Next
                </button>
              </div>
            )}
          </div>

          {/* FOOTER NAVIGATION */}
          <div className="cc-footer-nav">
            <button
              className="cc-btn-nav"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              <FaChevronLeft /> Previous
            </button>
            <button
              className="cc-btn-nav"
              onClick={handleNext}
              disabled={currentIndex === topics.length - 1 || currentIndex >= unlockedIndex}
            >
              Next <FaChevronRight />
            </button>
          </div>
        </main>
      </div>
    </>
  );
};

export default CourseAWS;
